/* eslint-disable no-console */
import constate from 'constate'
import keycloakInstance from 'keycloak-config'
import { useCallback, useEffect, useState } from 'react'

import { useLogoutAcrossTabs } from './logout-across-tabs'

type NotAuthUser = {
  state: 'unauthenticated'
}

export type AuthUserResponse = {
  accessToken: string
  message: string
}

type AuthUser = {
  state: 'authenticated'
} & AuthUserResponse

type User = NotAuthUser | AuthUser

export let globalAccessToken: string | null = null

export const setGlobalAccessToken = (accessToken: string | null) => {
  globalAccessToken = accessToken
}

const useAuth = () => {
  const [user, setUser] = useState<User>({ state: 'unauthenticated' })
  const [isInitialized, setIsInitialized] = useState(false)

  const login = useCallback(() => {
    keycloakInstance.login({
      redirectUri: window.location.origin,
    })
  }, [])

  const logout = useCallback(() => {
    keycloakInstance.logout({
      redirectUri: window.location.origin, // Redirect back to your app after logout
    })
  }, [])

  const unauthorize = useLogoutAcrossTabs(
    useCallback(() => {
      setGlobalAccessToken(null)
      setUser({ state: 'unauthenticated' })

      // Clear any stored tokens or user info
      localStorage.removeItem('user-token')
      sessionStorage.removeItem('user-token')
    }, []),
  )

  useEffect(() => {
    let isMounted = true

    const initKeycloak = async () => {
      try {
        const authenticated = await keycloakInstance.init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
          checkLoginIframe: false,
        })

        if (isMounted) {
          setIsInitialized(true)

          if (authenticated && keycloakInstance.token) {
            setUser({
              state: 'authenticated',
              accessToken: keycloakInstance.token,
              message: '',
            })
            setGlobalAccessToken(keycloakInstance.token)
          }
        }
      } catch (error) {
        console.error('Keycloak initialization failed:', error)
        if (isMounted) {
          unauthorize()
        }
      }
    }

    initKeycloak()

    return () => {
      isMounted = false
    }
  }, [unauthorize])

  return {
    user,
    login,
    logout, // Add logout method
    unauthorize,
    isAuthenticated: user.state === 'authenticated',
    state: user.state,
    isInitialized,
  } as const
}

export const [AuthProvider, useAuthContext] = constate(useAuth)
